import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { createSha256Hash } from '../../utils/createSha256Hash'
import { getAsset } from '../../utils/getAsset'
import { binanceApiEndpoint, binanceSymbolCryptoIdMap } from '../constants'
import { simpleEarnFlexiblePositionSchema } from './schema'

const responseSchema = z.object({
  rows: z.array(simpleEarnFlexiblePositionSchema),
  total: z.number(),
})

const urlPath = '/sapi/v1/simple-earn/flexible/position'

type FetchSimpleEarnFlexibleAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchSimpleEarnFlexibleAccountAssets = async ({
  apiKey,
  apiSecret,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchSimpleEarnFlexibleAccountAssetsParameters): Promise<
  readonly AssetBalance[]
> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, binanceApiEndpoint)
  url.searchParams.append('size', '100')
  url.searchParams.append('timestamp', timestamp.toString())
  url.searchParams.append(
    'signature',
    await createSha256Hash(url.searchParams.toString(), apiSecret),
  )

  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          'X-MBX-APIKEY': apiKey,
          ...proxyHeaders,
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.rows) {
    const asset = getAsset({
      symbol: assetData.asset,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: binanceSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    if (assetData.totalAmount.value <= 0n) {
      continue
    }
    assetBalances.push({
      key: `binance_simple-earn-flexible_${assetData.productId}`,
      cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
      fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
      amount: assetData.totalAmount,
      timestamp,
    })
  }

  return assetBalances
}
